/**
 * SupportTicketDetailPage — /support/tickets/:key (SCK-549 PR-8).
 *
 * DetailShell-based layout so the surface matches DirectoryUserDetail /
 * DirectoryRoleDetail visually. Renders:
 *   - Back link to /support + breadcrumb
 *   - Hero: ticket key + summary + status/SLA chips
 *   - Metadata SectionCard: status, priority, source, sla_due, jira link
 *   - Description SectionCard
 *   - Timeline SectionCard (stub — inline comments are a future PR)
 *
 * 404 renders an EmptyState (not a scary red error). Every network
 * error message is short + non-leaky per SOC 2.
 */

import { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Chip,
  Link as MuiLink,
  Skeleton,
  Stack,
  Typography,
} from "@mui/material";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import ConfirmationNumberOutlinedIcon from "@mui/icons-material/ConfirmationNumberOutlined";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import SearchOffIcon from "@mui/icons-material/SearchOff";
import { useParams } from "react-router-dom";

import {
  DetailShell,
  EmptyState,
  SectionCard,
} from "../../components/Directory/primitives";
import { formatIso, formatRelative } from "../../helpers/date";
import { getTicket } from "./api";
import {
  slaBadge,
  slaChipColor,
  statusPillColor,
} from "./statusHelpers";

function MetaRow({ label, children }) {
  return (
    <Stack
      direction={{ xs: "column", sm: "row" }}
      spacing={{ xs: 0.25, sm: 2 }}
      alignItems={{ sm: "baseline" }}
      sx={{ py: 0.75 }}
    >
      <Typography
        variant="caption"
        sx={{
          color: "text.secondary",
          textTransform: "uppercase",
          letterSpacing: "0.06em",
          minWidth: 120,
        }}
      >
        {label}
      </Typography>
      <Box sx={{ minWidth: 0, flex: 1 }}>{children}</Box>
    </Stack>
  );
}

export default function SupportTicketDetailPage() {
  const { key } = useParams();
  const [ticket, setTicket] = useState(null);
  const [err, setErr] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setTicket(null);
    setErr(null);
    setNotFound(false);
    getTicket(key)
      .then((data) => {
        if (!cancelled) setTicket(data);
      })
      .catch((e) => {
        if (cancelled) return;
        if (e?.status === 404) {
          setNotFound(true);
          return;
        }
        const msg =
          e?.status === 401
            ? "Sign in again to view this ticket."
            : e?.status === 403
              ? "You don't have permission to view this ticket."
              : "Couldn't load this ticket. Try again in a moment.";
        setErr(msg);
      });
    return () => {
      cancelled = true;
    };
  }, [key]);

  // ---- 404 ----------------------------------------------------------
  if (notFound) {
    return (
      <DetailShell
        backTo="/support"
        parentLabel="Support"
        parentTo="/support"
        title="Ticket not found"
      >
        <EmptyState
          icon={<SearchOffIcon />}
          title={`No ticket with key "${key}"`}
          description="It may have been deleted, or you may not have access to it. Head back to your ticket list to pick another."
          variant="no-match"
        />
      </DetailShell>
    );
  }

  // ---- Skeleton -----------------------------------------------------
  if (!ticket && !err) {
    return (
      <DetailShell
        backTo="/support"
        parentLabel="Support"
        parentTo="/support"
        title={<Skeleton width={320} />}
      >
        <Stack spacing={2}>
          <Skeleton variant="rectangular" height={140} />
          <Skeleton variant="rectangular" height={200} />
        </Stack>
      </DetailShell>
    );
  }

  // ---- Error --------------------------------------------------------
  if (err) {
    return (
      <DetailShell
        backTo="/support"
        parentLabel="Support"
        parentTo="/support"
        title="Support ticket"
      >
        <Alert severity="error">{err}</Alert>
      </DetailShell>
    );
  }

  // ---- Loaded -------------------------------------------------------
  const status = ticket.status || "Unknown";
  const priority = ticket.priority || "—";
  const source = ticket.source || "—";
  const slaDue = ticket.sla_due || ticket.slaDue;
  const updated = ticket.updated_at || ticket.updatedAt;
  const jiraUrl = ticket.jira_url || ticket.jiraUrl;
  const description = ticket.description || "";
  const badge = slaBadge(slaDue);

  return (
    <DetailShell
      backTo="/support"
      parentLabel="Support"
      parentTo="/support"
      avatar={
        <Box
          sx={{
            width: 44,
            height: 44,
            borderRadius: 1.5,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            bgcolor: (t) =>
              t.palette.mode === "dark"
                ? "rgba(249,115,22,0.15)"
                : "rgba(234,88,12,0.10)",
            color: "primary.main",
          }}
        >
          <ConfirmationNumberOutlinedIcon />
        </Box>
      }
      title={ticket.summary || ticket.key || key}
      description={
        <Box component="span" sx={{ fontFamily: "monospace" }}>
          {ticket.key || key}
        </Box>
      }
      badges={[
        <Chip
          key="status"
          label={status}
          size="small"
          color={statusPillColor(status)}
          variant="outlined"
        />,
        <Chip
          key="sla"
          label={badge.label}
          size="small"
          color={slaChipColor(badge.tone)}
          variant={badge.tone === "breached" ? "filled" : "outlined"}
        />,
      ]}
    >
      <Stack spacing={2}>
        <SectionCard title="Details">
          <MetaRow label="Status">
            <Chip
              label={status}
              size="small"
              color={statusPillColor(status)}
              variant="outlined"
            />
          </MetaRow>
          <MetaRow label="Priority">
            <Typography variant="body2">{priority}</Typography>
          </MetaRow>
          <MetaRow label="Source">
            <Typography variant="body2" sx={{ textTransform: "capitalize" }}>
              {source}
            </Typography>
          </MetaRow>
          <MetaRow label="SLA due">
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography variant="body2">
                {slaDue ? formatRelative(slaDue) : "—"}
              </Typography>
              {slaDue && (
                <Typography
                  variant="caption"
                  sx={{ color: "text.secondary" }}
                >
                  ({formatIso(slaDue)})
                </Typography>
              )}
            </Stack>
          </MetaRow>
          <MetaRow label="Updated">
            <Typography variant="body2">
              {updated ? formatRelative(updated) : "—"}
            </Typography>
          </MetaRow>
          {jiraUrl && (
            <MetaRow label="Jira">
              <MuiLink
                href={jiraUrl}
                target="_blank"
                rel="noopener noreferrer"
                underline="hover"
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 0.5,
                }}
              >
                Open in Jira
                <OpenInNewIcon fontSize="inherit" />
              </MuiLink>
            </MetaRow>
          )}
        </SectionCard>

        <SectionCard
          title="Description"
          isEmpty={!description}
          emptyState={{
            title: "No description",
            description:
              "This ticket was filed without a description block.",
          }}
        >
          <Typography
            variant="body2"
            component="div"
            sx={{
              whiteSpace: "pre-wrap",
              color: "text.primary",
              lineHeight: 1.55,
            }}
          >
            {description}
          </Typography>
        </SectionCard>

        <SectionCard
          title="Timeline"
          isEmpty
          emptyState={
            <EmptyState
              compact
              icon={<ChatBubbleOutlineIcon />}
              title="Timeline coming soon"
              description="Inline comments + status updates land in a follow-up. For now, updates flow through email."
              variant="no-match"
            />
          }
        />
      </Stack>
    </DetailShell>
  );
}
